<template>
  <nav class="mobile-nav">
    <router-link to="/" class="mobile-nav-link" active-class="active">
      <span class="mobile-nav-icon">📊</span>
      <span class="mobile-nav-text">首页</span>
    </router-link>
    <router-link to="/focus" class="mobile-nav-link" active-class="active">
      <span class="mobile-nav-icon">🎯</span>
      <span class="mobile-nav-text">专注</span>
    </router-link>
    <router-link to="/pending" class="mobile-nav-link" active-class="active">
      <span class="mobile-nav-icon">📝</span>
      <span class="mobile-nav-text">待处理</span>
      <span v-if="pendingCount > 0" class="mobile-nav-badge">{{ pendingCount }}</span>
    </router-link>
    <router-link to="/completed" class="mobile-nav-link" active-class="active">
      <span class="mobile-nav-icon">✅</span>
      <span class="mobile-nav-text">已完成</span>
    </router-link>

    <!-- 更多操作按钮 -->
    <button class="mobile-nav-link mobile-more-button" @click="showActionsMenu = !showActionsMenu" :class="{ active: showActionsMenu }">
      <span class="mobile-nav-icon">⚙️</span>
      <span class="mobile-nav-text">设置</span>
    </button>

    <!-- 操作菜单 -->
    <Teleport to="body">
      <Transition name="actions-menu">
        <div v-if="showActionsMenu" class="mobile-actions-overlay" @click.self="showActionsMenu = false">
          <div class="mobile-actions-menu">
            <!-- 用户信息头部 -->
            <div v-if="isAuthenticated" class="actions-user-header">
              <div class="actions-user-avatar">
                <img v-if="userAvatar" :src="userAvatar" alt="Avatar" />
                <span v-else class="avatar-placeholder">{{ userNickname.charAt(0).toUpperCase() }}</span>
              </div>
              <div class="actions-user-info">
                <div class="actions-user-name">{{ userNickname }}</div>
                <div class="actions-user-email">{{ userEmail }}</div>
              </div>
            </div>

            <!-- 操作选项列表 -->
            <div class="actions-list">
              <router-link to="/archived" class="action-item" @click="showActionsMenu = false">
                <span class="action-icon">📦</span>
                <span class="action-text">归档</span>
              </router-link>

              <router-link to="/tags" class="action-item" @click="showActionsMenu = false">
                <span class="action-icon">🏷️</span>
                <span class="action-text">标签</span>
              </router-link>

              <div class="actions-divider"></div>

              <button class="action-item" @click="handleThemeToggle">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
                <span class="action-text">明暗切换</span>
              </button>

              <button class="action-item" @click="handleColorPicker">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1 11.31 11.31"></path>
                  <path d="M5.69 5.69L12 12l6.31-6.31"></path>
                </svg>
                <span class="action-text">颜色主题</span>
              </button>

              <button class="action-item" @click="handleExport">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span class="action-text">导出数据</span>
              </button>

              <div v-if="isAuthenticated" class="actions-divider"></div>

              <button v-if="isAuthenticated" class="action-item" @click="openSettingsModal">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                <span class="action-text">个人设置</span>
              </button>

              <button v-if="isAuthenticated" class="action-item action-item-danger" @click="handleLogout">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span class="action-text">退出登录</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useItemsStore } from '../stores/items';
import { useUserStore } from '../stores/user';
import { useThemeStore } from '../stores/theme';
import { useToastStore } from '../stores/toast';

const itemsStore = useItemsStore();
const { items, pendingCount } = storeToRefs(itemsStore);

const userStore = useUserStore();
const { isAuthenticated, userEmail, userNickname, userAvatar } = storeToRefs(userStore);

const themeStore = useThemeStore();
const { toggleTheme } = themeStore;

const toast = useToastStore();

const showActionsMenu = ref(false);

function openSettingsModal() {
  showActionsMenu.value = false;
  window.dispatchEvent(new CustomEvent('open-user-settings'));
}

function handleLogout() {
  showActionsMenu.value = false;
  userStore.logout();
}

function handleThemeToggle() {
  toggleTheme();
  // 不关闭菜单，让用户看到效果
}

function handleColorPicker() {
  showActionsMenu.value = false;
  // 延迟触发，确保菜单先关闭
  setTimeout(() => {
    const event = new CustomEvent('open-color-picker');
    window.dispatchEvent(event);
  }, 100);
}

function handleExport() {
  showActionsMenu.value = false;
  // 延迟触发，确保菜单先关闭
  setTimeout(() => {
    const event = new CustomEvent('open-export', { detail: { items: items.value } });
    window.dispatchEvent(event);
  }, 100);
}
</script>

<style scoped>
.mobile-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  padding: 0;
  z-index: 1000;
  box-shadow: 0 -2px 8px var(--shadow-color);
}

.mobile-nav-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
  text-decoration: none;
  color: var(--text-secondary);
  padding: 0.5rem 0;
  transition: color 0.2s;
  position: relative;
}

.mobile-nav-link:hover {
  color: var(--color-primary);
}

.mobile-nav-link.active {
  color: var(--color-primary);
}

.mobile-nav-icon {
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.5rem;
  width: 1.5rem;
  line-height: 1;
}

.mobile-nav-icon svg {
  width: 1.5rem;
  height: 1.5rem;
}

.mobile-nav-text {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  text-align: center;
}

.mobile-nav-badge {
  position: absolute;
  top: 0.25rem;
  right: 20%;
  background: #ef4444;
  color: white;
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  border-radius: 10px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
}

/* 更多操作按钮 - 确保与其他链接占用相同空间 */
.mobile-more-button {
  border: none;
  background: transparent;
}

.mobile-more-button:hover {
  color: var(--color-primary);
}

.mobile-more-button.active {
  color: var(--color-primary);
}

/* 操作菜单遮罩 */
.mobile-actions-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  z-index: 2000;
  padding: 0;
}

/* 操作菜单 */
.mobile-actions-menu {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px 20px 0 0;
  padding: 1.5rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 暗色模式 */
:global(.dark) .mobile-actions-menu {
  background: rgba(42, 42, 42, 0.9);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* 用户信息头部 */
.actions-user-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-tertiary);
  border-radius: 12px;
  margin-bottom: 1rem;
}

.actions-user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--color-primary);
  flex-shrink: 0;
}

.actions-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.actions-user-info {
  flex: 1;
  overflow: hidden;
}

.actions-user-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.actions-user-email {
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 操作列表 */
.actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-tertiary);
  border: none;
  border-radius: 12px;
  font-size: 15px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
}

.action-item:hover {
  background: var(--bg-hover);
  transform: translateX(4px);
}

.action-item svg {
  flex-shrink: 0;
  color: var(--text-secondary);
}

.action-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
}

.action-text {
  flex: 1;
}

.action-item-danger {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.action-item-danger:hover {
  background: rgba(239, 68, 68, 0.15);
}

.action-item-danger svg {
  color: #ef4444;
}

.actions-divider {
  height: 1px;
  background: var(--border-color);
  margin: 0.5rem 0;
}

/* 菜单过渡动画 */
.actions-menu-enter-active,
.actions-menu-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.actions-menu-enter-from,
.actions-menu-leave-to {
  opacity: 0;
}

.actions-menu-enter-active .mobile-actions-menu,
.actions-menu-leave-active .mobile-actions-menu {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.actions-menu-enter-from .mobile-actions-menu,
.actions-menu-leave-to .mobile-actions-menu {
  transform: translateY(100%);
}

/* 暗夜模式 */
:global(.dark) .mobile-nav-link {
  color: var(--text-tertiary);
}

:global(.dark) .mobile-nav-link.active {
  color: var(--color-primary-light);
}

.avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: white;
  font-weight: 600;
  font-size: 20px;
}

/* 移动端显示 */
@media (max-width: 768px) {
  .mobile-nav {
    display: flex;
  }

  /* 为底部导航留出空间 */
  body {
    padding-bottom: 60px;
  }
}
</style>
